import React from 'react';
import {classes, style} from './doppe-layout-root.st.css';
import {DoppePageFooterBanner} from '../../../doppe-page-footer-banner/doppe-page-footer-banner';
import {
    DoppePagePrivacyAndTermsView
} from '../../../doppe-page-privacy-and-terms-view/doppe-page-privacy-and-terms-view';
import {useBymoPageContextProps} from '../../../../doppe-sdk';
import {
    DoppePageCookieBannerOpener
} from '../../../doppe-page-cookie-consent/doppe-page-cookie-banner-opener/doppe-page-cookie-banner-opener';
import {BymoPageViewerMode, useBymoPageViewerContext} from '../../../bymo-page-viewer-context/bymo-page-viewer-context';
import {useDoppeViewerLocales} from '../../../../client/doppe-viewer-locales/doppe-viewer-locales';
import {doppeViewerShouldRemoveHoppBanner} from '../../../doppe-viewer-utils/doppe-viewer-utils';
import {
    useDoppeViewerPageDesktopLayout, useDoppeViewerPrivacyAndTermsForDisplay,
    useDoppeViewerShouldShowCookieBanner
} from "../../../doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react";
import {evaluateFunction} from "@wix/devzai-utils-common";
import {
    DoppePageDesktopBackgroundView
} from "../../../doppe-page-desktop-background-view/doppe-page-desktop-background-view";
import {gridLayoutStretchAllChildren} from "@wix/devzai-utils-react";
import {DoppLayoutBackgroundMedia} from "../dopp-layout-background-media/dopp-layout-background-media";
import {BymoTemplateDesktopLayout, bymoTemplateDesktopLayoutIsSplitLayout} from "../../../../client-server-common";

const DoppeLayoutRootCssStateLayout = {
    FramedContent: 'framedContent',
    WithRightSideContent: 'withRightSideContent'
} as const;

export const DoppeLayoutRoot = React.memo(function DoppeLayoutDefaultProfileView(props: DoppeLayoutRoot.Props) {
    const {
        className,
        children,
        hideHoppBanner,
        sideContent,
        layoutBackground = null,

        ...htmlAttributes
    } = props;

    const doppeViewerLocales = useDoppeViewerLocales();
    const bymoPageContextProps = useBymoPageContextProps();
    const pageViewerContext = useBymoPageViewerContext();

    const showCookieBanner = useDoppeViewerShouldShowCookieBanner();
    const cookieBannerEnabled = (bymoPageContextProps.userCookieBannerSettings?.cookieBannerEnabled)
        && (pageViewerContext.mode === BymoPageViewerMode.Live);

    const hasPrivacyAndTerms = useDoppeViewerPrivacyAndTermsForDisplay() !== null;

    const removeHoppBanner = doppeViewerShouldRemoveHoppBanner({
        removeHoppBanner: hideHoppBanner
    }, bymoPageContextProps);

    const desktopLayout = useDoppeViewerPageDesktopLayout();
    const isSplitLayout = bymoTemplateDesktopLayoutIsSplitLayout(desktopLayout);

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, {
                hideFooterBanner: removeHoppBanner,
                hideCookieBanner: !cookieBannerEnabled,
                hidePrivacyAndTerms: !hasPrivacyAndTerms,
                desktopLayout: evaluateFunction(() => {
                    switch (desktopLayout) {
                        case BymoTemplateDesktopLayout.Split: {
                            return DoppeLayoutRootCssStateLayout.WithRightSideContent;
                        }
                        case BymoTemplateDesktopLayout.Standard: {
                            return DoppeLayoutRootCssStateLayout.FramedContent;
                        }
                    }
                }),
                isSplitLayout: isSplitLayout
            }, className)}
        >
            <div
                className={style(classes.main)}
            >
                {isSplitLayout ? (
                    <div
                        className={style(classes.splitLayoutContentBackground, {})}
                    >
                        <DoppLayoutBackgroundMedia
                        />
                    </div>
                ) : null}
                <div
                    className={style(classes.mainContent, {})}
                >
                    <div className={style(classes.backgroundWrapper, {})}>
                        {layoutBackground ? (
                            <div
                                className={style(classes.layoutBackgroundContainer, {})}
                            >
                                {layoutBackground}
                            </div>
                        ) : null}
                        {children}
                    </div>
                </div>
                {
                    hasPrivacyAndTerms ?
                        <div
                            className={style(classes.privacyAndTermsContainer, {})}
                        >
                            <DoppePagePrivacyAndTermsView
                                className={style(classes.privacyAndTerms, {})}
                            />
                        </div>
                        :
                        null
                }
                {isSplitLayout ? (
                    <div
                        className={style(classes.sideContentGridCell, {})}
                    >
                        <div
                            className={style(classes.sideContentContainer, gridLayoutStretchAllChildren())}
                        >
                            <DoppePageDesktopBackgroundView
                                className={style(classes.sideContentBackground, {})}
                            />
                            {sideContent}
                        </div>
                    </div>
                ) : null}
                {
                    cookieBannerEnabled && showCookieBanner ?
                        <DoppePageCookieBannerOpener
                            className={style(classes.cookieBanner, {})}
                        /> : null
                }
                {
                    !removeHoppBanner ?
                        <DoppePageFooterBanner
                            className={style(classes.contentFooter, {})}
                            bannerMessage={doppeViewerLocales['claim']}
                            targetUrl={`https://www.hopp.co`}
                        />
                        :
                        null
                }
            </div>
        </div>
    )
});

export namespace DoppeLayoutRoot {

    export interface Props extends React.HTMLAttributes<any> {
        hideHoppBanner: boolean;
        sideContent?: React.ReactNode;
        layoutBackground?: React.ReactElement | null;
    }
}